import React, { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup/dist/yup';

import { Input, Select, SubmissionButtons } from '@components/common';

import { useQueryApprovalUsersOptions } from '@hooks/approvelUnits/useQueryApprovalUsersOptions';

import { usersApprovalFormatted } from '@utils/formatter';

import { TNameTitleValueLabel } from '@interfaces/common/common';
import { TSelectOption } from '@interfaces/components/componentTypes';
import { IApprovalUnit } from '../interfaces';

import { ERevalidationMode, EValidationMode } from '@enums/validationMode';

import unitSchema from './schema';

import styles from './ApprovalUnitsForm.module.scss';

interface IUnitFormValues {
	unit: string;
	user: TNameTitleValueLabel[] | null;
}

export type TApprovalUnitsFormSubmitHandler = (value: { userIds: string[]; name?: string }) => void;

interface IApprovalUnitsFormProps {
	submitHandler: TApprovalUnitsFormSubmitHandler;
	editUnit: IApprovalUnit | null;
	setIsDirtyFormModal: React.Dispatch<React.SetStateAction<boolean>>;
	onClose: () => void;
}

const ApprovalUnitsForm = ({
	submitHandler,
	editUnit,
	setIsDirtyFormModal,
	onClose,
}: IApprovalUnitsFormProps) => {
	const {
		register,
		handleSubmit,
		formState: { errors, isDirty },
		watch,
		setValue,
		control,
	} = useForm<IUnitFormValues>({
		mode: EValidationMode.all,
		reValidateMode: ERevalidationMode.onChange,
		resolver: yupResolver(unitSchema),
		defaultValues: {
			unit: editUnit?.name,
			user: null,
		},
	});

	const { approvalUsersOptions, isLoadingApprovalUsersOptions } = useQueryApprovalUsersOptions();

	const users = watch('user');

	useEffect(() => setIsDirtyFormModal(isDirty), [isDirty, setIsDirtyFormModal]);

	useEffect(() => {
		if (editUnit?.users) setValue('user', usersApprovalFormatted(editUnit.users));
	}, [editUnit, setValue]);

	const onSubmit = (values: IUnitFormValues) => {
		const { user, unit } = values;

		if (!unit || !user?.length) return;

		submitHandler({
			userIds: user.map((item) => item.value),
			name: unit,
		});
	};

	const changeUsers = (options: Array<TSelectOption> | unknown) => {
		setValue('user', options as Array<TNameTitleValueLabel>);
	};

	return (
		<div
			className={styles.approvalUnitFormContainer}
			data-testid='approval-unit-form'
		>
			<div className={styles.inputContainer}>
				<Input
					errorMessage={errors.unit?.message}
					label='Approval unit name'
					name='unit'
					placeholder='Enter name'
					register={register as any}
					required
					setValue={setValue}
				/>
				<Select
					closeMenuOnSelect={false}
					control={control}
					disabled={isLoadingApprovalUsersOptions}
					errorMessage={errors.user?.message}
					handleChange={changeUsers}
					isClearable
					isError={!!errors?.user}
					isMulti
					label='Approval user'
					name='user'
					options={approvalUsersOptions}
					placeholder='Choose users'
					required
					value={users}
				/>
			</div>
			<SubmissionButtons
				negativeHandler={onClose}
				positiveHandler={handleSubmit(onSubmit)}
				positiveLabel={!editUnit ? 'Add' : 'Save'}
			/>
		</div>
	);
};

export default ApprovalUnitsForm;
